import { Config } from '@chegg-wt/core/config'
import React, { useState, useCallback, SyntheticEvent } from 'react'
import { connect } from 'react-redux'
import { basicProjectStylesSelector } from '@chegg-wt/core/state/projects/selectors'
import { AppState } from '@chegg-wt/core/state'
import { CancelIcon } from '@chegg-ui/theme'
import { useModalBehavior } from '@chegg-ui/modal'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  ModalWrapper,
  DefaultBackdrop,
  PositioningWrapper,
  ModalContent,
  CloseButton,
  Container,
} from '../styled'
import { MODAL_CLOSE_BUTTON, MODAL_CONTENT, MODAL_WRAPPER } from '../testIds'
import { BaseProjectModalProps } from './types'
import * as Styled from './styled'
import {
  PROJECT_MODAL_TITLE,
  PROJECT_MODAL_PROJECT_NAME,
  PROJECT_MODAL_STYLE_SELECT,
  PROJECT_MODAL_CREATE_EDIT_BUTTON,
} from './testIds'

const PROJECT_MODAL_ID = 'project-modal-wrapper'

export const BaseProjectModal: React.FunctionComponent<BaseProjectModalProps> = ({
  id,
  name,
  title,
  confirmText,
  action,
  close,
  currentProjectName = '',
  currentProjectStyle = Config.currentStyle,
  styles,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const { modalRef, onModalKeyDown } = useModalBehavior({
    isOpen,
    onClose: () => {
      close(name)
      setIsOpen(false)
    },
    id: PROJECT_MODAL_ID,
  })
  const [style, setStyle] = useState(currentProjectStyle)
  const [projectName, setProjectName] = useState(currentProjectName)

  const onConfirmClick = useCallback(() => {
    action(style, projectName, id)
    close(name)
  }, [action, style, projectName, id, close, name])

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    onConfirmClick()
  }

  const onOptionChange = useCallback(
    (e) => {
      const style = styles.find((style) => e.target.value === style.value)
      setStyle(style.value)
    },
    [styles]
  )

  return (
    <ModalWrapper
      backdrop
      show
      onBackdropClick={() => close(name)}
      renderBackdrop={() => <DefaultBackdrop />}
      data-test={MODAL_WRAPPER}
    >
      <PositioningWrapper onKeyDown={onModalKeyDown} ref={modalRef} id={PROJECT_MODAL_ID}>
        <ModalContent fullMobileModal data-test={MODAL_CONTENT} maxWidth={null} fullWidth>
          <CloseButton
            screenReaderOnlyText="close modal"
            onClick={() => close(name)}
            data-test={MODAL_CLOSE_BUTTON}
          >
            <CancelIcon />
          </CloseButton>
          <Container>
            <Styled.Container>
              <form onSubmit={onSubmit} autoComplete="off">
                <Styled.Header1 data-test={PROJECT_MODAL_TITLE}>{title}</Styled.Header1>
                <Styled.InputLabel htmlFor={PROJECT_MODAL_PROJECT_NAME}>
                  Name
                </Styled.InputLabel>
                <Styled.Input
                  id={PROJECT_MODAL_PROJECT_NAME}
                  data-test={PROJECT_MODAL_PROJECT_NAME}
                  type="text"
                  name="input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
                <Styled.InputLabel htmlFor={PROJECT_MODAL_STYLE_SELECT}>
                  Citation style
                </Styled.InputLabel>
                <Styled.BasicStyleSelect
                  id={PROJECT_MODAL_STYLE_SELECT}
                  onChange={onOptionChange}
                  defaultValue={style}
                  data-test={PROJECT_MODAL_STYLE_SELECT}
                >
                  {styles.map((basicStyle) => (
                    <option key={basicStyle.name} value={basicStyle.value}>
                      {basicStyle.name}
                    </option>
                  ))}
                </Styled.BasicStyleSelect>
                <Styled.FlexParentMargin>
                  <Styled.CreateEditButton
                    data-test={PROJECT_MODAL_CREATE_EDIT_BUTTON}
                    type="submit"
                  >
                    {confirmText}
                  </Styled.CreateEditButton>
                </Styled.FlexParentMargin>
              </form>
            </Styled.Container>
          </Container>
        </ModalContent>
      </PositioningWrapper>
    </ModalWrapper>
  )
}

const mapStateToProps = (state: AppState) => ({
  styles: basicProjectStylesSelector(state),
})

const mapDispatchToProps = {
  close: (name: string) => closeModal(name),
}

export default connect(mapStateToProps, mapDispatchToProps)(BaseProjectModal)
